import { defineComponent as _defineComponent } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { Bell, Loader2 as Loader } from 'lucide-vue-next';
import { PopoverRoot, PopoverTrigger } from 'reka-ui';
import { PopoverContent } from 'Components/primitives/popover';
import { EnTabs } from 'Components/base/EnTabs';
import EnNotificationListItem from '../EnNotificationListItem/EnNotificationListItem.vue';
import EnNotificationPushItem from '../EnNotificationPushItem/EnNotificationPushItem.vue';
import MarkAllNotificationsAsReadIcon from 'Components/icons/MarkAllNotificationsAsReadIcon.vue';
import NoNotificationsIcon from 'Components/icons/NoNotificationsIcon.vue';
import { Notification } from 'Js/signalR/notification';
import { NotificationType } from 'Js/enums/notificationType';
import { sleep } from 'Js/utils/sleep.js';
import { enFetch } from 'Js/utils/enFetch';
import { promiseForMinimumMs } from 'Js/utils/fetchUtils.js';
import { useNotificationState } from 'Components/notifications/composables/useNotificationState';
import { useGuidedTour } from 'Js/utils/useGuidedTour';
import { useUserSettingsStore } from 'Js/store/useUserSettingsStore';
import { useSkeletonComposable } from 'Components/utils/useSkeletonComposable';
import { EnSkeleton } from 'Components/base/EnSkeleton';
import NotificationCallback from 'Js/types/notifications/notificationCallback';
const guidedTourStepId = 'step-notify';
const callbackKey = 'global';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnNotificationsMenu',
    setup(__props, { expose: __expose }) {
        const { t } = useI18n();
        const state = useUserSettingsStore();
        const { isOpen } = storeToRefs(useNotificationState());
        const activeTab = ref(NotificationType.New);
        const loading = ref(false);
        const loadingMore = ref(false);
        const notifications = ref([]);
        const unreadNotifications = ref([]);
        const incomeNotifications = ref([]);
        const totalUnreadCount = ref(0);
        const domainUrl = computed(() => state?.IsAuthUrl ? '/api/Configuration' : '/api/Notifications');
        const { active: isGuidedTourActive, currentStepId } = useGuidedTour();
        /** While the guided tour highlights this step the menu must stay open. */
        const isPersistent = computed(() => isGuidedTourActive.value && currentStepId.value === guidedTourStepId);
        const { skeletonCount } = useSkeletonComposable(450, 85);
        let page = 0;
        let allItemsLoaded = false;
        const readNotifications = computed(() => Array.isArray(notifications.value) ? notifications.value.filter(n => n.readAt !== null) : []);
        const unreadNotificationsCount = computed(() => totalUnreadCount.value);
        const tabs = computed(() => [
            { id: NotificationType.New, label: t('common.notifications.new') },
            { id: NotificationType.Read, label: t('common.notifications.all') },
        ]);
        /** Items rendered for a given tab. */
        function itemsForTab(tabId) {
            return tabId === NotificationType.New ? unreadNotifications.value : readNotifications.value;
        }
        const canMarkAllAsRead = computed(() => activeTab.value === NotificationType.New && unreadNotificationsCount.value > 0);
        /**
         * Marks a notification as read by sending a POST request to the server.
         * @param notification
         * @param rerenderUi
         */
        async function markNotificationAsRead(notification, rerenderUi = false) {
            await enFetch(`${domainUrl.value}/UpdateNotificationRead?timestamp=${encodeURIComponent(notification.timestamp.toString())}`, { method: 'POST' });
            if (!rerenderUi)
                return;
            const index = unreadNotifications.value.findIndex(n => n.timestamp === notification.timestamp);
            if (index !== -1) {
                const updated = { ...unreadNotifications.value[index], readAt: new Date().toISOString() };
                unreadNotifications.value.splice(index, 1);
                notifications.value.push(updated);
            }
        }
        /**
         * Marks all unread notifications as read, then reloads both lists.
         */
        async function markAllNotificationsAsRead() {
            loading.value = true;
            const fetchPromise = enFetch(`${domainUrl.value}/MarkAllAsRead`, {
                method: 'POST',
                // MarkAllAsRead has no response body, so it must not be parsed as json
                noResponseBody: true,
            });
            await promiseForMinimumMs(fetchPromise).then(async (resp) => {
                if (resp[0].ok) {
                    await Promise.all([
                        loadNotificationsPaginated(NotificationType.New, true),
                        loadNotificationsPaginated(NotificationType.Read, true),
                    ]);
                }
                loading.value = false;
            });
        }
        /**
         * Prepares the loading state and pagination parameters for the notification request.
         * @param reset - Whether to reset the pagination state
         */
        function prepareLoadingState(reset = false) {
            if (reset) {
                loading.value = true;
                page = 0;
                allItemsLoaded = false;
            }
            else {
                loadingMore.value = true;
            }
        }
        /**
         * Prepares the request parameters based on notification type.
         * @param notificationType - The type of notifications to load
         */
        function prepareRequestParams(notificationType) {
            const params = new URLSearchParams({ skip: String(page * 10), take: '10' });
            if (notificationType === NotificationType.Read) {
                params.append('isRead', 'true');
            }
            else if (notificationType === NotificationType.New) {
                params.append('isRead', 'false');
            }
            return params;
        }
        /**
         * Updates the local state with the received notifications.
         * @param resItems - The notifications received from the server
         * @param reset - Whether this is a reset load or a pagination load
         */
        function updateNotificationsState(resItems, reset) {
            if (page !== 0 && !reset) {
                notifications.value.push(...resItems);
            }
            else {
                notifications.value = resItems;
            }
            unreadNotifications.value = notifications.value.filter(n => n.readAt === null);
            if (resItems.length > 0) {
                page++;
            }
            else {
                allItemsLoaded = true;
            }
        }
        /**
         * Main function to load notifications with pagination support.
         * @param notificationType - The type of notifications to load
         * @param reset - Whether to reset pagination and loading state
         */
        async function loadNotificationsPaginated(notificationType = NotificationType.New, reset = false) {
            try {
                prepareLoadingState(reset);
                const params = prepareRequestParams(notificationType);
                const response = (await enFetch(`${domainUrl.value}/GetNotifications?${params}`, {
                    noResponseBody: true,
                }));
                if (response.ok) {
                    const responseData = await response.json();
                    const resItems = responseData.Notifications.map((n) => Notification.from(n));
                    if (notificationType === NotificationType.New) {
                        totalUnreadCount.value = responseData.TotalCount;
                    }
                    updateNotificationsState(resItems, reset);
                }
            }
            finally {
                await sleep(500);
                loading.value = false;
                loadingMore.value = false;
            }
        }
        /**
         * Loads the next page once the user scrolls to the bottom of the list.
         * @param event
         */
        function onScroll(event) {
            const target = event.target;
            const atBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
            if (atBottom && !allItemsLoaded && !loadingMore.value) {
                loadNotificationsPaginated(activeTab.value, false);
            }
        }
        const pushMessage = (notification) => {
            incomeNotifications.value.unshift(notification);
            unreadNotifications.value.unshift(notification);
            totalUnreadCount.value++;
            setTimeout(() => removeNotificationFromCenter(notification), 5000);
        };
        __expose({ pushMessage });
        const removeNotificationFromCenter = (notification) => {
            incomeNotifications.value = incomeNotifications.value.filter(m => m.timestamp !== notification.timestamp);
        };
        /** Keeps the popover open while the guided tour points at it. */
        function onCloseAttempt(event) {
            if (isPersistent.value)
                event.preventDefault();
        }
        watch(activeTab, tab => loadNotificationsPaginated(tab, true));
        watch(isPersistent, persistent => {
            if (!persistent && isOpen.value) {
                isOpen.value = false;
            }
        }, { flush: 'post' });
        watch(state, (newVal) => {
            if (newVal?.state?.NotificationCallbacks && !newVal?.state.NotificationCallbacks.find((callback) => callback.key === callbackKey)) {
                newVal?.state?.AddNotificationCallback?.(new NotificationCallback(callbackKey, pushMessage, true));
            }
        });
        onMounted(() => loadNotificationsPaginated(activeTab.value, true));
        const __returned__ = { t, state, isOpen, activeTab, loading, loadingMore, notifications, unreadNotifications, incomeNotifications, totalUnreadCount, domainUrl, guidedTourStepId, isGuidedTourActive, currentStepId, isPersistent, skeletonCount, get page() { return page; }, set page(v) { page = v; }, get allItemsLoaded() { return allItemsLoaded; }, set allItemsLoaded(v) { allItemsLoaded = v; }, readNotifications, unreadNotificationsCount, tabs, itemsForTab, canMarkAllAsRead, markNotificationAsRead, markAllNotificationsAsRead, prepareLoadingState, prepareRequestParams, updateNotificationsState, loadNotificationsPaginated, onScroll, pushMessage, callbackKey, removeNotificationFromCenter, onCloseAttempt, get Bell() { return Bell; }, get Loader() { return Loader; }, get PopoverRoot() { return PopoverRoot; }, get PopoverTrigger() { return PopoverTrigger; }, get PopoverContent() { return PopoverContent; }, get EnTabs() { return EnTabs; }, EnNotificationListItem, EnNotificationPushItem, MarkAllNotificationsAsReadIcon, NoNotificationsIcon, get NotificationType() { return NotificationType; }, get EnSkeleton() { return EnSkeleton; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
